import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Send,
  Search,
  Loader2,
  Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export function SendToUserDialog({ ideaId, ideaTitle, open, onOpenChange }) {
  const { token } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [step, setStep] = useState("select"); // "select" | "message" | "done"

  useEffect(() => {
    if (open) {
      setStep("select");
      setSelectedUser(null);
      setMessage("");
      setSearchQuery("");
      setUsers([]);
    }
  }, [open]);

  const searchUsers = useCallback(async (q) => {
    if (!q.trim()) {
      setUsers([]);
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/users/available?q=${encodeURIComponent(q)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(res.data.users);
    } catch {
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    const timer = setTimeout(() => searchUsers(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery, searchUsers]);

  const handleSend = async () => {
    if (!selectedUser || !ideaId) return;
    setSending(true);
    try {
      await axios.post(`${API}/messages/send`, {
        idea_id: ideaId,
        recipient_id: selectedUser.id,
        message: message || null,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStep("done");
      toast.success(`Ideia enviada para ${selectedUser.name}!`);
      setTimeout(() => onOpenChange(false), 1500);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao enviar ideia");
    } finally {
      setSending(false);
    }
  };

  const getInitials = (name) => {
    return name?.split(" ").map(n => n[0]).slice(0, 2).join("").toUpperCase() || "?";
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" style={{ background: 'var(--vi-card-solid)', border: '1px solid var(--vi-border-accent)' }}>
        <DialogHeader>
          <DialogTitle className="font-heading" style={{ color: 'var(--vi-text)' }}>
            {step === "done" ? "Enviado!" : "Enviar Ideia"}
          </DialogTitle>
          <DialogDescription>
            {step === "select" && "Busque e selecione um usuário da plataforma"}
            {step === "message" && `Enviando "${ideaTitle}" para ${selectedUser?.name}`}
            {step === "done" && "Sua ideia foi enviada com sucesso!"}
          </DialogDescription>
        </DialogHeader>

        <div className="py-2 space-y-3">
          {step === "select" && (
            <>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--vi-text-muted)' }} />
                <Input
                  placeholder="Buscar por nome..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  style={{ background: 'var(--vi-input-bg)', borderColor: 'var(--vi-border-accent)', color: 'var(--vi-text)' }}
                  autoFocus
                  data-testid="send-user-search"
                />
              </div>

              {loading && (
                <div className="flex justify-center py-4">
                  <Loader2 className="w-5 h-5 animate-spin text-indigo-500" />
                </div>
              )}

              {!loading && searchQuery.trim() && users.length === 0 && (
                <div className="text-center py-6">
                  <Users className="w-8 h-8 mx-auto mb-2" style={{ color: 'var(--vi-text-muted)', opacity: 0.3 }} />
                  <p className="text-xs" style={{ color: 'var(--vi-text-muted)' }}>
                    Nenhum usuário encontrado com esse nome
                  </p>
                  <p className="text-[10px] mt-1" style={{ color: 'var(--vi-text-muted)', opacity: 0.7 }}>
                    O usuário precisa ter habilitado o recebimento de ideias
                  </p>
                </div>
              )}

              {!loading && !searchQuery.trim() && (
                <div className="text-center py-6">
                  <Search className="w-8 h-8 mx-auto mb-2" style={{ color: 'var(--vi-text-muted)', opacity: 0.3 }} />
                  <p className="text-xs" style={{ color: 'var(--vi-text-muted)' }}>
                    Digite o nome do usuário para buscar
                  </p>
                </div>
              )}

              <div className="space-y-1 max-h-48 overflow-y-auto">
                {users.map(u => (
                  <button
                    key={u.id}
                    onClick={() => { setSelectedUser(u); setStep("message"); }}
                    className="w-full text-left px-3 py-2.5 rounded-xl flex items-center gap-3 hover:bg-indigo-500/10 transition-colors"
                    style={{ color: 'var(--vi-text)' }}
                    data-testid={`select-user-${u.id}`}
                  >
                    <div className="w-9 h-9 rounded-full bg-indigo-500/20 text-indigo-400 flex items-center justify-center text-sm font-semibold">
                      {getInitials(u.name)}
                    </div>
                    <span className="text-sm font-medium">{u.name}</span>
                  </button>
                ))}
              </div>
            </>
          )}

          {step === "message" && (
            <>
              {/* Selected user */}
              <div className="flex items-center gap-3 p-3 rounded-xl" style={{ background: 'var(--vi-input-bg)', border: '1px solid var(--vi-border-accent)' }}>
                <div className="w-10 h-10 rounded-full bg-indigo-500/20 text-indigo-400 flex items-center justify-center text-sm font-semibold">
                  {getInitials(selectedUser?.name)}
                </div>
                <div>
                  <p className="text-sm font-medium" style={{ color: 'var(--vi-text)' }}>{selectedUser?.name}</p>
                  <p className="text-xs" style={{ color: 'var(--vi-text-muted)' }}>Receberá sua ideia</p>
                </div>
              </div>

              {/* Optional message */}
              <div>
                <label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--vi-text-soft)' }}>
                  Mensagem (opcional)
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Dê uma olhada nessa ideia..."
                  className="w-full h-20 rounded-xl px-3 py-2 text-sm resize-none"
                  style={{ background: 'var(--vi-input-bg)', border: '1px solid var(--vi-border-accent)', color: 'var(--vi-text)' }}
                  data-testid="send-message-input"
                />
              </div>

              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  onClick={() => { setStep("select"); setSelectedUser(null); }}
                  className="flex-1 border"
                  style={{ borderColor: 'var(--vi-border-accent)', color: 'var(--vi-text-soft)' }}
                  data-testid="send-back-button"
                >
                  Voltar
                </Button>
                <Button
                  onClick={handleSend}
                  disabled={sending}
                  className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white gap-2"
                  data-testid="send-confirm-button"
                >
                  {sending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Enviar
                    </>
                  )}
                </Button>
              </div>
            </>
          )}

          {step === "done" && (
            <div className="text-center py-4">
              <div className="w-12 h-12 rounded-full bg-emerald-500/20 mx-auto flex items-center justify-center mb-3">
                <Send className="w-5 h-5 text-emerald-400" />
              </div>
              <p className="text-sm font-medium" style={{ color: 'var(--vi-text)' }}>
                Ideia enviada para {selectedUser?.name}!
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
